import { useEffect, useRef } from "react";
import { useConnectionState, useZero } from "@rocicorp/zero/react";

/**
 * Reactively recovers from auth-driven Zero disconnects.
 *
 * When Zero reports `connectionState.name === "needs-auth"` for an
 * auth-bearing reason (mutate / query 401 / 403, or a zero-cache auth
 * error), mints a fresh Zero session token via the supplied refresh
 * callback and reconnects. Debounced to one in-flight attempt at a time
 * with a 5s cool-down between attempts.
 *
 * Renders nothing — purely effectful.
 */
export function ZeroAuthRecovery({
  refreshZeroSessionToken,
}: {
  refreshZeroSessionToken: () => Promise<string | null>;
}) {
  const zero = useZero();
  const connectionState = useConnectionState();
  const recoveryState = useRef({ inFlight: false, lastAttemptAt: 0 });

  useEffect(() => {
    if (connectionState.name !== "needs-auth") return;
    const reason = connectionState.reason;
    if (
      reason.type !== "mutate" &&
      reason.type !== "query" &&
      reason.type !== "zero-cache"
    ) {
      return;
    }
    if (
      (reason.type === "mutate" || reason.type === "query") &&
      reason.status !== 401 &&
      reason.status !== 403
    ) {
      return;
    }

    const now = Date.now();
    if (recoveryState.current.inFlight) return;
    if (now - recoveryState.current.lastAttemptAt < 5_000) return;

    let cancelled = false;
    recoveryState.current.inFlight = true;
    recoveryState.current.lastAttemptAt = now;

    void (async () => {
      const token = await refreshZeroSessionToken();
      if (!token || cancelled) return;
      await zero.connection.connect({ auth: token });
    })().finally(() => {
      recoveryState.current.inFlight = false;
      recoveryState.current.lastAttemptAt = Date.now();
    });

    return () => {
      cancelled = true;
    };
  }, [connectionState, refreshZeroSessionToken, zero]);

  return null;
}
